import { Component, EventEmitter, inject, Input, Output, signal, DestroyRef } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, switchMap, take } from 'rxjs/operators';
import { AppConfirmService } from 'src/app/shared/services/app-confirm/app-confirm.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { NotificationService } from 'src/app/notification.service';
import { Chunk } from '../../chunk.dto';
import { ChunkService } from '../../chunk.service';

@Component({
  selector: 'app-analyze-media-option',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, TranslateModule, MatProgressSpinnerModule],
  template: `
    <div class="interactive-item" (click)="analyzeMedia()">
      @if (isLoading()) {
        <mat-spinner diameter="20" class="interactive-item__icon"></mat-spinner>
      } @else {
        <mat-icon class="interactive-item__icon" aria-hidden="true">summarize</mat-icon>
      }
      <span class="interactive-item__text">
        {{ 'ChunkController.Options.AnalyzeMedia' | translate }}
      </span>
    </div>
  `,
})
export class AnalyzeMediaOptionComponent {
  @Input() chunk!: Chunk;
  @Input() contentID = '';
  @Output() actionTriggered = new EventEmitter<void>();

  private chunkService = inject(ChunkService);
  private toast = inject(ToastService);
  private translate = inject(TranslateService);
  private confirmService = inject(AppConfirmService);
  private notificationService = inject(NotificationService);
  private destroyRef = inject(DestroyRef);

  isLoading = signal(false);

  analyzeMedia() {
    if (this.isLoading()) return;

    this.confirmService
      .confirm({
        title: this.translate.instant('ChunkController.Options.AnalyzeMedia'),
        message: this.translate.instant('ChunkController.Options.AnalyzeMediaConfirm'),
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          this.isLoading.set(true);
          const contentId = this.contentID || (this.chunk as any).contentID || '';
          const mediaType = this.chunk.type as 'image' | 'video';

          const trigger$ = this.chunkService.analyzeMedia(this.chunk._id, contentId, mediaType);
          const update$ = this.notificationService.videoAnalysisComplete$.pipe(
            filter((notification) => notification && notification.chunkId === this.chunk._id),
            take(1)
          );

          trigger$
            .pipe(
              switchMap(() => {
                this.toast.snackbar.open(this.translate.instant('ChunkController.Options.AnalyzeMediaStarted'), 'Ok', {
                  duration: 5000,
                });
                this.actionTriggered.emit(); // Dismiss the overlay early while it runs in background
                return update$;
              }),
              takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
              next: () => {
                this.isLoading.set(false);
                this.toast.snackbar.open(
                  this.translate.instant('ChunkController.Options.AnalyzeMediaFinished') || 'Video summarized successfully!',
                  'Ok',
                  { duration: 3000 }
                );
              },
              error: () => {
                this.isLoading.set(false);
                this.toast.snackbar.open(this.translate.instant('Snackbar.Error'), 'Ok', {
                  duration: 3000,
                });
              },
            });
        }
      });
  }
}
